import { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Send, 
  Check, 
  CheckCircle, 
  MoreHorizontal,
  Trash2,
  Edit2,
  SmilePlus
} from 'lucide-react';
import { Button } from '../ui/button';

const EMOJI_OPTIONS = ['👍', '❤️', '😊', '🎉', '🤔', '👀'];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const ReactionButton = ({ reactions, currentUserId, onReact }) => {
  const [showPicker, setShowPicker] = useState(false);

  // Group reactions by emoji
  const groupedReactions = reactions.reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = [];
    acc[r.emoji].push(r);
    return acc;
  }, {});

  return (
    <div className="relative">
      <button
        onClick={() => setShowPicker(!showPicker)}
        className="p-1 hover:bg-gray-100 rounded transition-colors"
        title="Add reaction"
      >
        <SmilePlus size={14} className="text-gray-400" />
      </button>

      {showPicker && (
        <div className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 p-1 flex gap-1 z-50">
          {EMOJI_OPTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => {
                onReact(emoji);
                setShowPicker(false);
              }}
              className="w-7 h-7 hover:bg-gray-100 rounded flex items-center justify-center text-sm transition-colors"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Display existing reactions */}
      {Object.keys(groupedReactions).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(groupedReactions).map(([emoji, users]) => {
            const hasReacted = users.some((u) => u.user_id === currentUserId);
            return (
              <button
                key={emoji}
                onClick={() => onReact(emoji)}
                className={`px-1.5 py-0.5 rounded-full text-xs flex items-center gap-1 transition-colors ${
                  hasReacted
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={users.map((u) => u.user_name).join(', ')}
              >
                <span>{emoji}</span>
                <span>{users.length}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MentionInput = ({ value, onChange, onSubmit, placeholder, collaborators = [] }) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
    if (e.key === '@') {
      setShowMentions(true);
      setMentionFilter('');
    }
    if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Check for @ mentions
    const lastAtIndex = newValue.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = newValue.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setShowMentions(true);
        setMentionFilter(textAfterAt.toLowerCase());
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = value.lastIndexOf('@');
    const newValue = value.slice(0, lastAtIndex) + `@${user.name} `;
    onChange(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredCollaborators = collaborators.filter(
    (c) =>
      c.name.toLowerCase().includes(mentionFilter) ||
      c.email.toLowerCase().includes(mentionFilter)
  );

  return (
    <div className="relative">
      <textarea
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        rows={2}
      />

      {showMentions && filteredCollaborators.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-full max-h-40 overflow-y-auto z-50">
          {filteredCollaborators.map((user) => (
            <button
              key={user.id}
              onClick={() => insertMention(user)}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                <div className="text-xs text-gray-500">{user.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const CommentThread = ({
  comment,
  onClose,
  onReply,
  onResolve,
  onDelete,
  onReact,
  onReplyReact,
  currentUserId,
  collaborators = [],
  position,
}) => {
  const [replyText, setReplyText] = useState('');
  const [showActions, setShowActions] = useState(false);

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    
    // Extract mentions from text
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(replyText)) !== null) {
      const mentioned = collaborators.find(
        (c) => c.name.toLowerCase() === match[1].toLowerCase()
      );
      if (mentioned) mentions.push(mentioned.id);
    }

    onReply(replyText, mentions);
    setReplyText('');
  };

  const isOwn = comment.user_id === currentUserId;

  return (
    <div
      className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 w-80 max-h-[500px] flex flex-col z-50"
      style={{
        left: Math.min(position.x, window.innerWidth - 340),
        top: Math.min(position.y, window.innerHeight - 520),
      }}
      data-testid="comment-thread"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">Comment</span>
          {comment.resolved && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              Resolved
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!comment.resolved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onResolve(true)}
              className="h-7 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              <CheckCircle size={14} className="mr-1" />
              Resolve
            </Button>
          )}
          {comment.resolved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onResolve(false)}
              className="h-7 px-2 text-xs"
            >
              Reopen
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* Comment content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Original comment */}
        <div className="group">
          <div className="flex items-start gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                isOwn ? 'bg-blue-500' : 'bg-purple-500'
              }`}
            >
              {comment.user_name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {comment.user_name}
                </span>
                <span className="text-xs text-gray-400">
                  {formatDate(comment.created_at)}
                </span>
                {isOwn && (
                  <div className="relative ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setShowActions(!showActions)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <MoreHorizontal size={14} className="text-gray-400" />
                    </button>
                    {showActions && (
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <button
                          onClick={() => {
                            onDelete();
                            setShowActions(false);
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                {comment.content}
              </p>
              <ReactionButton
                reactions={comment.reactions || []}
                currentUserId={currentUserId}
                onReact={(emoji) => onReact(emoji)}
              />
            </div>
          </div>
        </div>

        {/* Replies */}
        {comment.replies?.map((reply, idx) => {
          const isReplyOwn = reply.user_id === currentUserId;
          return (
            <div key={reply.id || idx} className="pl-11 group">
              <div className="flex items-start gap-3">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                    isReplyOwn ? 'bg-blue-500' : 'bg-purple-500'
                  }`}
                >
                  {reply.user_name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {reply.user_name}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatDate(reply.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                    {reply.content}
                  </p>
                  <ReactionButton
                    reactions={reply.reactions || []}
                    currentUserId={currentUserId}
                    onReact={(emoji) => onReplyReact(reply.id, emoji)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply input */}
      {!comment.resolved && (
        <div className="p-3 border-t border-gray-100">
          <div className="flex gap-2">
            <div className="flex-1">
              <MentionInput
                value={replyText}
                onChange={setReplyText}
                onSubmit={handleSubmitReply}
                placeholder="Reply or @mention..."
                collaborators={collaborators}
              />
            </div>
            <Button
              size="sm"
              onClick={handleSubmitReply}
              disabled={!replyText.trim()}
              className="self-end"
            >
              <Send size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
